<!-- The reader's boxes (`message-form.component.less` and the skin's `knowledge.css`), block for block, in Tailwind at
     `--spacing: 2px` (a class number is half the pixels). Every block is a `stch-skeleton-block` in Gray/200, with the
     shared block's default pulse. -->
<!-- The row is measured as the reader measures its `.heading`: narrower than 500px, the pane is contracted. -->
<div
  class="flex min-h-32 items-start gap-2 px-8 pt-6"
  data-testid="mail-reader-skeleton-sender"
  aria-hidden="true"
  (resize)="handleResize($event)"
>
  <!-- `.meta`'s 0 4px, then `app-message-connections`: the 40px avatar 4px down, the two 24px lines 8px right of it. -->
  <div class="flex min-w-0 flex-1 gap-4 px-2">
    <stch-skeleton-block
      class="mt-2 size-20 flex-none rounded-full bg-gray-200"
      data-testid="mail-reader-skeleton-avatar"
    />
    <span class="flex min-w-0 flex-col">
      <span class="flex h-12 items-center" data-testid="mail-reader-skeleton-sender-line"
        ><stch-skeleton-block class="h-8 w-100 flex-none bg-gray-200"
      /></span>
      <span class="flex h-12 items-center" data-testid="mail-reader-skeleton-sender-line"
        ><stch-skeleton-block class="h-8 w-130 flex-none bg-gray-200"
      /></span>
    </span>
  </div>
  <!-- `.heading-end`: the icon row, then the mailbox badge 8px under it. The row in the reader's order: Reply, Reply
       all, Forward, the workspace chip, Assign, Share, More, Close; a contracted pane folds Reply to Forward and Share
       away. Inside a team the 32px workspace chip makes the row 32 high and the header 68. -->
  <div class="flex shrink-0 flex-col items-end gap-4">
    <span class="flex items-center gap-4" data-testid="mail-reader-skeleton-actions" [class.h-16]="workspace()">
      @for (action of leadingActions(); track action) {
        <stch-skeleton-block class="size-10 flex-none bg-gray-200" data-testid="mail-reader-skeleton-action" />
      }
      @if (workspace()) {
        <stch-skeleton-block
          class="h-16 w-35 flex-none rounded-lg bg-gray-200"
          data-testid="mail-reader-skeleton-workspace"
        />
      }
      @if (handover()) {
        <stch-skeleton-block
          class="h-16 w-62 flex-none rounded-lg bg-gray-200"
          data-testid="mail-reader-skeleton-assign"
        />
      }
      @for (action of trailingActions(); track action) {
        <stch-skeleton-block class="size-10 flex-none bg-gray-200" data-testid="mail-reader-skeleton-action" />
      }
    </span>
    <stch-skeleton-block class="h-8 w-23 flex-none bg-gray-200" />
  </div>
</div>

<!-- The reader's `.subject`, with the real subject and date in the reader's type, so neither moves on arrival. -->
<div class="flex min-h-20 flex-wrap items-end justify-between gap-x-6 py-2 pr-8 pl-10">
  <div
    class="min-w-0 flex-1 font-[Inter] text-lg leading-12 font-medium tracking-0 text-gray-950 [overflow-wrap:anywhere]"
    data-testid="mail-reader-skeleton-subject"
  >
    @if (message().title) {
      {{ message().title }}
    } @else {
      <span class="font-normal text-gray-500">No subject</span>
    }
  </div>
  <!-- The date, with the team label 4px before it on a team-mailbox email, at the row's right edge. -->
  <div class="ml-auto flex items-center gap-2">
    @if (workspace() === 'team') {
      <stch-skeleton-block
        class="size-12 flex-none rounded-full bg-gray-200"
        data-testid="mail-reader-skeleton-team-label"
      />
    }
    @if (message().date) {
      <div
        class="pt-2 font-[Inter] text-sm leading-12 font-normal tracking-1 text-subdued"
        data-testid="mail-reader-skeleton-date"
      >
        {{ message().date | dateFormat: 'dateTime' }}
      </div>
    } @else {
      <!-- An open from a search suggestion knows the subject but not the date: a bar holds the date's place. -->
      <span class="flex h-12 items-center pt-2" data-testid="mail-reader-skeleton-date-bar"
        ><stch-skeleton-block class="h-8 w-50 flex-none bg-gray-200"
      /></span>
    }
  </div>
</div>

<!-- The reader's `.tabs`: 42 high with its one Gray/200 rule, the tabs 4px apart, the Knowledge button at the end. -->
<div class="flex h-21 flex-none items-center gap-2 overflow-hidden border-b border-gray-200 px-8" aria-hidden="true">
  @for (tab of tabs(); track $index) {
    <span class="flex flex-none justify-center" data-testid="mail-reader-skeleton-tab" [style.width.px]="tab.width">
      <stch-skeleton-block class="h-8 flex-none bg-gray-200" [style.width.px]="tab.bar" />
    </span>
  }
  <span class="flex-1"></span>
  <stch-skeleton-block class="size-10 flex-none bg-gray-200" />
</div>

@if (handover()) {
  <!-- The reader's "Responsible: … · Created by: …" line under the tabs (`mx-8 my-2`, 16px of 12px text). -->
  <div class="mx-8 my-2 flex h-8 flex-none items-center" data-testid="mail-reader-skeleton-handover" aria-hidden="true">
    <stch-skeleton-block class="h-6 w-70 flex-none bg-gray-200" />
  </div>
}

@if (knowledge()) {
  <!-- The skin's Knowledge box (`knowledge.css`) as it looks while its rows load: 8px / 12px round a Blue/50 card, three
       rows of 32, 33 and 33 (a row after the first carries the 1px rule), each led by its 16px Gray/500 icon 7px under
       the row's top, closed by a Gray/200 rule. The chips are the rows' own loading chips, pulsing as they do. -->
  <div class="flex-none border-b border-gray-200" data-testid="mail-reader-skeleton-knowledge" aria-hidden="true">
    <div class="mx-6 my-4 overflow-hidden rounded-lg border border-gray-200 bg-primary-50">
      @for (row of knowledgeRows; track row.icon; let first = $first) {
        <div
          class="flex items-center px-4 pt-1"
          data-testid="mail-reader-skeleton-knowledge-row"
          [class]="first ? 'h-16' : 'h-16.5 border-t border-gray-200'"
        >
          <span class="mr-2 inline-flex h-10 shrink-0 items-center text-gray-500">
            <app-icon [name]="row.icon" [size]="16" />
          </span>
          @for (chip of row.chips; track $index) {
            <stch-skeleton-block [class]="chip" />
          }
        </div>
      }
    </div>
  </div>
}

<!-- What the reader draws above the body, when the row already says it will: the Other Mail notice, and for any
     calendar mail the invitation card's 16px gap, with the card itself in its first, "Loading calendar event..."
     state only for a REQUEST — the one method the card is drawn for (the boxes of `message-form.component.html` and
     `mail-invitation-card.component.html`). -->
@if (message().otherMail) {
  <div
    class="mx-8 mb-4 flex min-h-17 flex-none items-center rounded-lg border border-gray-200 px-8 py-4"
    data-testid="mail-reader-skeleton-notice"
    aria-hidden="true"
  >
    <stch-skeleton-block class="h-6 w-210 max-w-full flex-none bg-gray-200" />
  </div>
}
@if (message().eventRemoteUid) {
  <div class="flex-none pt-8" data-testid="mail-reader-skeleton-invitation" aria-hidden="true">
    @if (message().eventMethod === 'REQUEST') {
      <div
        class="mx-8 mb-4 flex min-h-25 items-center gap-4 rounded-lg border border-gray-200 p-8"
        data-testid="mail-reader-skeleton-invitation-card"
      >
        <!-- The card's icon slot: no size, so it draws nothing and only keeps the 8px gap before the line. -->
        <stch-skeleton-block class="flex-none bg-gray-200" />
        <stch-skeleton-block class="h-6 w-70 flex-none bg-gray-200" />
      </div>
    }
  </div>
}

<!-- The body: the mail frame starts 18px under the body's top and 16px in; text lines on a 24px pitch. -->
<div class="flex flex-col px-8 pt-9" data-testid="mail-reader-skeleton-body" aria-hidden="true">
  @for (width of bodyLines; track $index) {
    <span class="flex h-12 items-center">
      @if (width) {
        <stch-skeleton-block
          class="h-6 flex-none bg-gray-200"
          data-testid="mail-reader-skeleton-text"
          [style.width]="width"
        />
      }
    </span>
  }
</div>
